import { ReactNode } from 'react';

import { MoneyMessage } from '../../../types/MoneyMessage';
import { Text } from '../../cdl/Text/Text';
import { DataSheet } from '../../common/DataSheet/DataSheet';
import { VerticalDetail } from '../../common/Detail/VerticalDetail';
import { createdBy } from '../../common/helpers/createdBy.helper';
import { formatCompanyName } from '../../common/helpers/formatCompanyName.helper';
import { formatDate } from '../../common/helpers/formatDate.helper';
import { formatMoney } from '../../common/helpers/formatMoney.helper';
import { translate } from '../../common/helpers/translate.helper';
import { Characters } from '../../common/helpers/unicode.helper';
import { HistoricBuyerRefPopover } from '../../common/HistoricBuyerRefPopover/HistoricBuyerRefPopover';
import { useEnums } from '../../common/hooks/useEnums';
import { usePaymentTermsFormat } from '../../common/hooks/usePaymentTermsFormat';
import { useRole } from '../../common/hooks/useRole';
import { InternalNamePopover } from '../../common/InternalNamePopover';
import { Box } from '../../common/ui/Box';
import { Port } from '../../port/Port';
import { OfferModel } from '../model/OfferModel';

import { OfferDataSheetValue } from './OfferDatasheetValue';

interface CustomerOfferDatasheetProps {
    offer: OfferModel;
    fee?: MoneyMessage;
    variant?: 'datasheet' | 'verticalDetail';
}

interface DetailProps {
    label: string;
    value: ReactNode;
    variant: CustomerOfferDatasheetProps['variant'];
}

const Detail = ({ label, value, variant }: DetailProps) => {
    if (variant === 'verticalDetail') {
        return <VerticalDetail label={label} value={value} />;
    }

    return (
        <>
            <DataSheet.Label>{label}</DataSheet.Label>
            {value}
        </>
    );
};

export const OfferDatasheet = ({ offer, fee, variant = 'datasheet' }: CustomerOfferDatasheetProps) => {
    const role = useRole();
    const { getHumanReadableValue } = useEnums();
    const formatPaymentTerms = usePaymentTermsFormat();
    const showContract = (role.isCustomer() || role.isSuperAdmin()) && offer.lubesContract?.name;

    const makeCustomerWording = () => {
        return role.isCustomer()
            ? translate('offer.detail.coreData.company')
            : translate('offer.detail.coreData.customer');
    };

    const makeSupplierWording = () => {
        return role.isSupplier()
            ? translate('offer.detail.coreData.company')
            : translate('offer.detail.coreData.supplier');
    };

    const getSupplyModeString = () => {
        return getHumanReadableValue('supplyMode', offer.supplyMode);
    };

    const details = (
        <>
            {role.isCustomer() ? (
                <Detail
                    variant={variant}
                    label={makeCustomerWording()}
                    value={
                        <Box display="flex" alignItems="center" gap={8}>
                            <Text>{formatCompanyName({ company: offer.customer })}</Text>
                            <InternalNamePopover company={offer.customer} />
                        </Box>
                    }
                />
            ) : null}

            {role.isSupplier() ? (
                <Detail
                    variant={variant}
                    label={makeSupplierWording()}
                    value={
                        <Box display="flex" alignItems="center" gap={8}>
                            <Text>{formatCompanyName({ company: offer.supplier })}</Text>
                            <InternalNamePopover company={offer.supplier} />
                        </Box>
                    }
                />
            ) : null}

            {offer.port ? (
                <Detail
                    variant={variant}
                    label={translate('offer.detail.coreData.port')}
                    value={
                        <OfferDataSheetValue changed={offer.changes.portChanged}>
                            <Port port={offer.port} vesselId={offer.vesselId} showTooltip />
                        </OfferDataSheetValue>
                    }
                />
            ) : null}

            <Detail
                variant={variant}
                label={translate('offer.detail.coreData.dateDelivery')}
                value={
                    <OfferDataSheetValue changed={offer.changes.dateDeliveryChanged}>
                        {formatDate({ date: offer.dateDelivery, timeZone: 'UTC' })}
                    </OfferDataSheetValue>
                }
            />

            {offer.buyerReference || (!role.isSupplier() && offer.historicBuyerReferences?.length) ? (
                <Detail
                    variant={variant}
                    label={translate('offer.detail.coreData.buyerReference')}
                    value={
                        <Box
                            display="inline-flex"
                            alignItems="center"
                            color={offer.changes.buyerReferenceChanged ? 'attention.foreground' : 'foreground.default'}
                            gap={4}
                        >
                            <Text>{offer.buyerReference || Characters.EN_DASH}</Text>
                            {!role.isSupplier() && offer.historicBuyerReferences?.length ? (
                                <HistoricBuyerRefPopover references={offer.historicBuyerReferences} />
                            ) : null}
                        </Box>
                    }
                />
            ) : null}

            {offer.vendorReference ? (
                <Detail
                    variant={variant}
                    label={translate('offer.detail.coreData.vendorReference')}
                    value={
                        <OfferDataSheetValue changed={offer.changes.vendorReferenceChanged}>
                            {offer.vendorReference}
                        </OfferDataSheetValue>
                    }
                />
            ) : null}

            {offer.supplyMode ? (
                <Detail
                    variant={variant}
                    label={translate('offer.detail.coreData.supplyMode')}
                    value={
                        <OfferDataSheetValue changed={offer.changes.supplyModeChanged}>
                            {getSupplyModeString()}
                        </OfferDataSheetValue>
                    }
                />
            ) : null}

            {offer.noticeDays ? (
                <Detail
                    variant={variant}
                    label={translate('offer.detail.coreData.noticeDays')}
                    value={
                        <OfferDataSheetValue changed={offer.changes.noticeDaysChanged}>
                            {offer.noticeDays}
                        </OfferDataSheetValue>
                    }
                />
            ) : null}

            {offer.isSpot() ? (
                <Detail
                    variant={variant}
                    label={translate('offer.detail.coreData.paymentTermsLabel')}
                    value={
                        <OfferDataSheetValue
                            changed={
                                offer.changes.paymentTermReferenceChanged ||
                                offer.changes.paymentTermReferenceDaysChanged
                            }
                        >
                            {offer.paymentTermReference
                                ? formatPaymentTerms(offer.paymentTermReference, offer.paymentTermReferenceDays)
                                : Characters.EN_DASH}
                        </OfferDataSheetValue>
                    }
                />
            ) : null}

            <Detail
                variant={variant}
                label={translate('offer.detail.coreData.offerNumber')}
                value={<Text>{offer.offerNumber}</Text>}
            />

            {showContract ? (
                <Detail
                    variant={variant}
                    label={translate('offer.detail.coreData.contract')}
                    value={<Text>{offer.lubesContract?.name}</Text>}
                />
            ) : null}

            {role.isSupplier() && fee ? (
                <Detail
                    variant={variant}
                    label={translate('offer.detail.coreData.transactionFee')}
                    value={
                        <Text>
                            {formatMoney({
                                value: fee.value,
                                currency: fee.currency,
                                minimumFractionDigits: 2,
                            })}
                        </Text>
                    }
                />
            ) : null}

            <Detail
                variant={variant}
                label={translate('offer.detail.coreData.dateCreated')}
                value={<Text>{createdBy({ order: offer })}</Text>}
            />
        </>
    );

    if (variant === 'verticalDetail') {
        return (
            <Box display="grid" gap={16}>
                {details}
            </Box>
        );
    }

    return <DataSheet>{details}</DataSheet>;
};
